import React, {useCallback, useEffect, useMemo, useState} from 'react';
import { Theme, useTheme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import Drawer from '@mui/material/Drawer';
import { throttle, debounce } from 'throttle-debounce';
import {
  Avatar,
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Select,
  Switch,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Slider from "@mui/material/Slider";
import Input from "@mui/material/Input";
import {useSelector, useDispatch} from 'react-redux';
import {
  ColorMapKey, ColorType,
  selectCloudDistance, changeCloudDistance,
  selectOpacity, changeOpacity,
  selectCloudScale, changeCloudScale,
  selectCloudMinimum, changeCloudMinimum,
  selectHeightClip, changeHeightClip,
  selectIntensityClip, changeIntensityClip,
  selectColorMap, changeColorMap,
  selectColorType, changeColorType,
  selectDynamicSize, changeDynamicSize,
  changeClassificationVisibility,
  changeAllClassificationVisibility,
  changeCircularPoints, selectCircularPoints,
  changeArrowMarkers, selectArrowMarkers, MarkerNavType,
  selectCloudMaxPoints, changeCloudMaxPoints,
  selectClassifications,
  getAvailableClassifications,
  getClassificationVisibilities,
  changeAvailableClassifications,
  changeTagDistance,
  selectTagDistance,
  ClassificationSetting
} from '../redux/settings-slice';
import {colorMaps, getColorMap} from "../viewer/textures/colormaps";
import { getClassificationColors } from "../classifications";
import { isDevMode } from '../electron-modules';
import { ArrowTooltip, SlimScrollbar, SwitchWithLabel } from '../components';
import LocalScene from "../viewer/js/projections";
import { UnitConverter } from "../viewer/js/utilities";
import { useTranslation } from "react-i18next";
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import {isEqual} from 'lodash';
import { headerHeight } from '../app';
import { nanoid } from '@reduxjs/toolkit';
import clsx from 'clsx';
import { useViewer } from '../hooks';

export const settingsDrawerWidth = 280;

const converter = new UnitConverter();

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    backdrop: {
      display: "none"
    },
    drawer: {
      width: 0,
      "z-index": `${theme.zIndex.appBar - 1} !important`,
    },
    drawerPaper: {
      width: settingsDrawerWidth,
      paddingTop: `${headerHeight}px`,
      height: `calc(100% - ${headerHeight}px)`,
    },
    asterisk: {
      "&:after": {
        content:'"*"',
        color: "red",
        paddingLeft: theme.spacing(0.5)
      }
    },
    drawerHeader: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0, 1),
      // necessary for content to be below app bar
      ...theme.mixins.toolbar,
      justifyContent: 'flex-start',
      minHeight: `${headerHeight}px`
    },
    list: {
      width: '100%',
      maxWidth: 360,
      backgroundColor: theme.palette.background.paper,
    },
    listItem: {
      width: '100%',
      marginBottom: theme.spacing(0.5)
    },
    formControl: {
      width: '100%',
      marginBottom: theme.spacing(1)
    },
    switch: {
      marginLeft: 0
    },
    colorPreview: {
      height: theme.spacing(3),
      width: theme.spacing(3),
      marginRight: theme.spacing(1),
    },
    img: {
      objectFit: 'fill',
      transform: 'rotate(-90deg)'
    },
    classificationPreview: {
      height: theme.spacing(1.25),
      width: theme.spacing(1.25),
      backgroundColor: '#bbb',
      borderRadius: '50%',
      borderColor: '#000000',
      borderStyle: 'solid',
      borderWidth: '1px',
      marginRight: theme.spacing(1),
      display: 'inline-block'
    },
    inputText: {
      float: "right",
      paddingRight: "10px"
    },
    inputPropParent: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      zoom: "0.85",
      marginRight: `calc(36px + ${theme.spacing(1.5)})`,
    },
    inputPropTitle: {
      height: "fit-content"
    },
    title: {
      flex: 1,
      margin: theme.spacing(0)
    },
    boldTitleTypography: {
      fontWeight: 'bold',
      fontSize: '1em'
    },
    sectionTitle: {
      display: "inline-block",
      fontWeight: 'bold',
      fontSize: '0.95em'
    },
    rightInput: {
      marginRight: "4px"
    }
  }),
);

const updateValueInterval = 50;
const saveDispatchInterval = 1000;
const classificationInterval = 200;

interface GenericSliderProps {
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value) => void;
  onFinish?: (value) => void;
  labelFunction?: (value) => string;
  magnitude?: number;
  units?: string;
  endAdornment?: string;
}

interface InputSliderProps extends GenericSliderProps {
  type: "single" | "range";
}

const InputSlider = (props: InputSliderProps) => {
  const {value, type, onChange, onFinish, labelFunction,
    magnitude=1.0, units="m", endAdornment=""} = props;

  const min = converter.convert(props.min * magnitude, "m", units);
  const max = converter.convert(props.max * magnitude, "m", units);
  const step = props.step * magnitude;

  const classes = useStyles();
  const theme = useTheme();

  const isRangeType = type === "range";
  const inputProps = {min, max, step};

  const defaultLabel = value => {
    return `${+Number(value).toFixed(2)}${endAdornment}`;
  };

  const labelFormat = labelFunction
    ? labelFunction
    : defaultLabel;

  const [openState, setOpen] = useState(false);
  const [sliderValue, setSliderValue] = useState(null);

  const roundToStep = (value) => {
    return +Number((Math.round(value/step) * step).toFixed(2));
  };

  const toInput = (value) => {
    if (isRangeType) {
      const min = converter.convert(value.min * magnitude, "m", units);
      const max = converter.convert(value.max * magnitude, "m", units);

      return [roundToStep(min), roundToStep(max)];
    }

    const out = converter.convert(value * magnitude, "m", units);
    return roundToStep(out);
  };

  const fromInput = (value) => {
    if (isRangeType) {
      return {
        min: converter.convert(value[0], units, "m") / magnitude,
        max: converter.convert(value[1], units, "m") / magnitude
      };
    }

    return converter.convert(value, units, "m") / magnitude;
  };

  // Throttle update function
  const changeCallback = useCallback(
    throttle(updateValueInterval, (value) => {
      onChange(value);
    }), []);

  // Debounce save function
  const finishCallback = useCallback(
    debounce(saveDispatchInterval, (value) => {
      if (!onFinish) return;
      onFinish(value);
    }), []);

  useEffect(() => {
    let scaledValue = toInput(value);

    /** NOTE: react will never return equal for arrays, so lodash is used */
    if (isEqual(sliderValue, scaledValue)) return;
    setSliderValue(scaledValue);
  }, [value, units]);

  useEffect(() => {
    if (sliderValue === null) return;

    // Trigger update and change callbacks
    const saveValue = fromInput(sliderValue);
    changeCallback(saveValue);
    finishCallback(saveValue);
  }, [sliderValue]);

  const handleClick = () => {
    setOpen(!openState);
  };

  return (
    <React.Fragment>
      <div style={{
        display: "flex",
        alignItems: "flex-end",
        paddingTop: theme.spacing(1.0)
      }}>
        <div style={{
          width: "100%"
        }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}>
            <Typography variant="body2">{labelFormat(min)}</Typography>
            <Typography variant="body2">{labelFormat(max)}</Typography>
          </Box>

          <Slider
            min={min}
            max={max}
            step={step}
            value={sliderValue}
            color={"secondary"}
            valueLabelDisplay="auto"
            valueLabelFormat={labelFormat}
            onChange={(event, value) => {
              setSliderValue(value);
            }}
            aria-labelledby="input-slider"
          />
        </div>

        <IconButton
          size="small"
          style={{
            marginLeft: theme.spacing(1.5),
            height: "fit-content"
          }}
          onClick={(event) => {
            event.stopPropagation();
            handleClick();
          }}
        >
          {openState ? <KeyboardArrowUp  fontSize="small"/> : <KeyboardArrowDown fontSize="small"/>}
        </IconButton>
      </div>

      {openState && (<div className={classes.listItem}>
        {isRangeType && (<RangeInputProp
          values={sliderValue}
          setValues={setSliderValue}
          inputProps={inputProps}
          adornment={endAdornment}
        />)}

        {!isRangeType && (<SingleInputProp
          value={sliderValue}
          setValue={setSliderValue}
          inputProps={inputProps}
          adornment={endAdornment}
        />)}
      </div>)}
    </React.Fragment>
  );
};

interface InputProp {
  min: number;
  max: number;
  step: number;
}

interface SingleInputPropProps {
  value: number;
  setValue: React.Dispatch<number>;
  inputProps: InputProp;
  adornment: string;
}

const SingleInputProp = (props: SingleInputPropProps) => {
  const {value, setValue, inputProps, adornment} = props;

  const classes = useStyles();
  const {t} = useTranslation();

  const [inputValue, setInputValue] = useState(0);
  const [inputError, setInputError] = useState(false);

  useEffect(() => {
    setInputValue(+Number(value).toFixed(2));
    setInputError(false);
  }, [value]);

  const hasError = (value) => {
    return (value === "")
     || isNaN(value)
     || (parseFloat(value) < inputProps.min)
     || (parseFloat(value) > inputProps.max);
  };

  return (
    <div className={classes.inputPropParent}>
      <InputLabel className={classes.inputPropTitle}>
        {t("user-settings.slider-value")}:
      </InputLabel>

      <InputField
        value={inputValue}
        inputProps={inputProps}
        error={inputError}
        adornment={adornment}
        onChange={(value) => {
          // Check for general error
          let error = hasError(value);
          setInputValue(value);
          setInputError(error);
          if (error) return;

          // Update slider value
          value = parseFloat(value);
          setValue(value);
        }}
      />
    </div>
  );
};

interface RangeInputPropProps {
  values: number[];
  setValues: React.Dispatch<number[]>;
  inputProps: InputProp;
  adornment: string;
}

const RangeInputProp = (props: RangeInputPropProps) => {
  const {values, setValues, inputProps, adornment} = props;

  const classes = useStyles();
  const {t} = useTranslation();

  const [minValue, setMinValue] = useState(0);
  const [maxValue, setMaxValue] = useState(0);

  const [minError, setMinError] = useState(false);
  const [maxError, setMaxError] = useState(false);

  useEffect(() => {
    setMinValue(+Number(values[0]).toFixed(2));
    setMaxValue(+Number(values[1]).toFixed(2));

    setMinError(false);
    setMaxError(false);
  }, [values]);

  const hasError = (value) => {
    return (value === "")
     || isNaN(value)
     || (parseFloat(value) < inputProps.min)
     || (parseFloat(value) > inputProps.max);
  };

  return (
    <React.Fragment>
      <div className={classes.inputPropParent}>
        <InputLabel className={classes.inputPropTitle}>
          {t("user-settings.slider-minimum-value")}:
        </InputLabel>

        <InputField
          value={minValue}
          inputProps={inputProps}
          adornment={adornment}
          error={minError}
          onChange={(value) => {
            // Check for general error
            let error = hasError(value);
            setMinValue(value);
            setMinError(error);
            if (error) return;

            value = parseFloat(value);

            // Make sure min is less than max
            if (value > maxValue) {
              setMinError(true);
              return;
            }

            // Update slider values
            setValues([value, maxValue]);
          }}
        />
      </div>

      <div className={classes.inputPropParent}>
        <InputLabel className={classes.inputPropTitle}>
          {t("user-settings.slider-maximum-value")}:
        </InputLabel>

        <InputField
          value={maxValue}
          inputProps={inputProps}
          adornment={adornment}
          error={maxError}
          onChange={(value) => {
            // Check for general error
            let error = hasError(value);
            setMaxValue(value);
            setMaxError(error);
            if (error) return;

            value = parseFloat(value);

            // Make sure max is more than min
            if (value < minValue) {
              setMaxError(true);
              return;
            }

            // Update slider values
            setValues([minValue, value]);
          }}
        />

      </div>
    </React.Fragment>
  );
};

interface InputFieldProps {
  value: number;
  inputProps: InputProp;
  onChange: (value) => void;
  adornment: string;
  error: boolean;
}

const InputField = (props: InputFieldProps) => {
  const {value, inputProps, onChange, adornment, error} = props;

  return (
    <Input
      fullWidth
      error={error}
      style={{
        flex: 1
      }}
      inputProps={{
        ...inputProps,
        type: "number"
      }}
      value={value}
      endAdornment={<InputAdornment position="end">{adornment}</InputAdornment>}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );
};

export const SingleSlider = (props: GenericSliderProps) => {
  return <InputSlider type="single" {...props}/>;
};

export const RangeSlider = (props: GenericSliderProps) => {
  return <InputSlider type="range" {...props}/>;
};

interface ClassificationListProps {
  classifications: ClassificationSetting[];
}

const ClassificationList = (props: ClassificationListProps) => {
  const {classifications} = props;

  const dispatch = useDispatch();
  const classes = useStyles();
  const {t} = useTranslation();

  const [classificationColors, setClassificationColors] = useState(
    Array(256).fill('#ffffff'));

  useEffect(() => {
    const updateColorsList = async () => {
      const colors = await getClassificationColors();
      setClassificationColors(colors);
    };

    updateColorsList();
  }, []);

  const hasClassifications = classifications.length > 0;

  return (
    <React.Fragment>
      {!hasClassifications && (
        <div>
          <ListItem>
            <Typography gutterBottom variant="subtitle2">
              {t("user-settings.no-points-loaded")}
            </Typography>
          </ListItem>
        </div>
      )}

      {hasClassifications && (
        classifications.map(classification => (
          <ListItem key={classification.id}>
            <div className={classes.listItem}>
              <SwitchWithLabel
                label={`${classification.name} (${classification.id})`}
                startAdornment={
                  <span
                    className={classes.classificationPreview}
                    style={{
                      backgroundColor: classificationColors[classification.id]
                    }}
                  />
                }
                checked={classification.visible}
                onChange={(_, checked) => {
                  dispatch(changeClassificationVisibility(classification.id));
                }}
              />
            </div>
          </ListItem>
        ))
      )}

    </React.Fragment>
  );
};

interface SettingsDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const SettingsDrawer = (props: SettingsDrawerProps) => {
  const {open, onClose} = props;

  const dispatch = useDispatch();
  const classes = useStyles();
  const {viewer} = useViewer();
  const {t} = useTranslation();

  const classifications = useSelector(selectClassifications);

  /** Checkbox selectors (uses redux value directly) */
  const savedColorMap = useSelector(selectColorMap);
  const savedColorType = useSelector(selectColorType);
  const savedDynamicSize = useSelector(selectDynamicSize);
  const savedCircularPoints = useSelector(selectCircularPoints);
  const savedArrowMarkers = useSelector(selectArrowMarkers);

  /** Developer only toggles */
  const [pickerDebug, setPickerDebug] = useState(false);
  const [boundsVisible, setBoundsVisible] = useState(false);
  const [renderPaused, setRenderPaused] = useState(false);

  /** Slider selectors (uses throttled save callback) */
  const savedOpacity = useSelector(selectOpacity);
  const savedTagDistance = useSelector(selectTagDistance);
  const savedCloudDistance = useSelector(selectCloudDistance);
  const savedCloudScale = useSelector(selectCloudScale);
  const savedCloudMinimum = useSelector(selectCloudMinimum);
  const savedMaxPoints = useSelector(selectCloudMaxPoints);
  const savedHeightClip = useSelector(selectHeightClip);
  const savedIntensityClip = useSelector(selectIntensityClip);

  const paperPropID = useState(nanoid())[0];

  const savedAvailableClassifications = useMemo(() => {
    return getAvailableClassifications(classifications);
  }, [classifications]);

  const savedClassificationVisibilities = useMemo(() => {
    return getClassificationVisibilities(classifications);
  }, [classifications]);

  const units = LocalScene.viewProjectionUnits;

  /** Handle classification updates */
  useEffect(() => {
    const interval = setInterval(() => {
      if (!viewer) return;

      const newClasses = viewer.getAvailableClassifications();
      const oldClasses = savedAvailableClassifications.map(x => x.id);

      // NOTE: Sorting is important for equality check
      newClasses.sort();
      oldClasses.sort();

      if (isEqual(newClasses, oldClasses)) return;

      dispatch(changeAvailableClassifications(newClasses));
    }, classificationInterval);

    return () => {
      clearInterval(interval);
    };
  }, [viewer, classifications]);

  useEffect(() => {
    viewer?.setPointCloudOpacity(savedOpacity);
  }, [viewer, savedOpacity]);

  useEffect(() => {
    viewer?.setTagDistance(savedTagDistance);
  }, [viewer, savedTagDistance]);

  useEffect(() => {
    viewer?.setPointCloudDistance(savedCloudDistance);
  }, [viewer, savedCloudDistance]);

  useEffect(() => {
    viewer?.setPointCloudScale(savedCloudScale);
  }, [viewer, savedCloudScale]);

  useEffect(() => {
    viewer?.setPointCloudMinimum(savedCloudMinimum);
  }, [viewer, savedCloudMinimum]);

  useEffect(() => {
    viewer?.setPointCloudMaxPoints(savedMaxPoints);
  }, [viewer, savedMaxPoints]);

  useEffect(() => {
    viewer?.setPointCloudHeightClip(savedHeightClip);
  }, [viewer, savedHeightClip]);

  useEffect(() => {
    viewer?.setPointCloudIntensityClip(savedIntensityClip);
  }, [viewer, savedIntensityClip]);

  useEffect(() => {
    viewer?.setVisibleClassifications(savedClassificationVisibilities);
  }, [viewer, savedClassificationVisibilities]);

  useEffect(() => {
    viewer?.setPointCloudColorMap(savedColorMap);
  }, [viewer, savedColorMap]);

  useEffect(() => {
    viewer?.setPointCloudColorType(savedColorType);
  }, [viewer, savedColorType]);

  useEffect(() => {
    viewer?.setPointCloudDynamicSize(savedDynamicSize);
  }, [viewer, savedDynamicSize]);

  useEffect(() => {
    viewer?.setPointCloudCircularPoints(savedCircularPoints);
  }, [viewer, savedCircularPoints]);

  useEffect(() => {
    viewer?.setNavigationType(savedArrowMarkers);
  }, [viewer, savedArrowMarkers]);

  const isIntensity = savedColorType === 0;
  const isHeightmap = savedColorType === 2;
  const colorMapRequired = isIntensity || isHeightmap;

  return (
    <Drawer
      className={classes.drawer}
      anchor="right"
      open={open}
      transitionDuration={0}
      onFocus={() => {
        /** Remove tabindex from paper to stop drag/drop issues */
        let element = document.getElementById(paperPropID);
        element?.removeAttribute("tabindex");
      }}
      classes={{
        paper: classes.drawerPaper,
      }}
      slotProps={{
        backdrop: {
          classes: {
            root: classes.backdrop
          }
        }
      }}
      PaperProps={{
        id: paperPropID
      }}
    >
      <SlimScrollbar>

        <List dense className={classes.list}>
          <ListItem>
            <Typography
              className={clsx(classes.boldTitleTypography, classes.title)}
              gutterBottom
            >
              {t('user-settings.project_settings')}
            </Typography>

            {/* Close button */}
            <IconButton aria-label="close" onClick={onClose} size="large">
              <CloseIcon fontSize="small"/>
            </IconButton>
          </ListItem>
        </List>

        {/* Developer Controls */}
        {isDevMode && (<React.Fragment>
          <List dense className={classes.list}>
            <ListItem>
              <Typography className={classes.sectionTitle} gutterBottom>
                {t('user-settings.developer-debug')}
              </Typography>
            </ListItem>

            {/* Picker Debug */}
            <ListItem>
              <div className={classes.listItem}>
                <SwitchWithLabel
                  label={t("user-settings.gpu-picker")}
                  checked={pickerDebug}
                  onChange={(_, checked) => {
                    viewer?.togglePickerDebug(checked);
                    setPickerDebug(checked);
                  }}
                />
              </div>
            </ListItem>

            {/* Points render toggle */}
            <ListItem>
              <div className={classes.listItem}>
                <SwitchWithLabel
                  label={t('user-settings.pause-points-rendering')}
                  checked={renderPaused}
                  onChange={(_, checked) => {
                    viewer?.setPointsShouldUpdate(!checked);
                    setRenderPaused(checked);
                  }}
                />
              </div>
            </ListItem>

            {/* Point Bounds Debug */}
            <ListItem>
              <div className={classes.listItem}>
                <SwitchWithLabel
                  label={t('user-settings.visible-point-bounds')}
                  checked={boundsVisible}
                  onChange={(_, checked) => {
                    viewer?.setPointBoundsVisible(checked);
                    setBoundsVisible(checked);
                  }}
                />
              </div>
            </ListItem>
          </List>

          <Divider variant="middle" />
        </React.Fragment>)}

        {/* Point Cloud Controls */}
        <List dense className={classes.list}>
          <ListItem>
            <Typography className={classes.sectionTitle} gutterBottom>
              {t("user-settings.point-cloud-controls")}
            </Typography>
          </ListItem>

          {/* Color type */}
          <ListItem>
            <div className={classes.listItem}>
              <Typography gutterBottom variant="subtitle2">
                {t("user-settings.color-type")}
              </Typography>
              <FormControl className={classes.formControl}>
                <Select
                  value={savedColorType}
                  onChange={(event) => {
                    const value = event.target.value as ColorType;
                    dispatch(changeColorType(value));
                  }}
                >
                  <MenuItem key={0} value={0}>
                    {t("color-type.intensity")}
                  </MenuItem>
                  <MenuItem key={1} value={1}>
                    {t("color-type.color")}
                  </MenuItem>
                  <MenuItem key={2} value={2}>
                    {t("color-type.height")}
                  </MenuItem>
                  <MenuItem key={3} value={3}>
                    {t("color-type.classification")}
                  </MenuItem>
                </Select>
              </FormControl>
            </div>
          </ListItem>

          {/* Color maps */}
          {colorMapRequired && (
            <ListItem>
              <div className={classes.listItem}>
                <Typography gutterBottom variant="subtitle2">
                  {t("user-settings.color-map")}
                </Typography>
                <FormControl className={classes.formControl}>
                  <Select
                    value={savedColorMap}
                    renderValue={(value) => {
                      return getColorMap(value).name;
                    }}
                    onChange={(event) => {
                      const value = event.target.value as ColorMapKey;
                      dispatch(changeColorMap(value));
                    }}
                  >
                    {colorMaps.map((colorMap) => (
                      <MenuItem key={colorMap.id} value={colorMap.id}>
                        <Avatar
                          classes={{ img: classes.img }}
                          src={colorMap.image}
                          className={classes.colorPreview}
                        />
                        {colorMap.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </ListItem>
          )}

          {/* Arrow Markers */}
          <ListItem>
            <div className={classes.listItem}>
              <Typography gutterBottom variant="subtitle2">
                {t("user-settings.navigation-type")}
              </Typography>
              <FormControl className={classes.formControl}>
                <Select
                  value={savedArrowMarkers}
                  onChange={(event) => {
                    let value = event.target.value as MarkerNavType;
                    dispatch(changeArrowMarkers(value));
                  }}
                >
                  <MenuItem key={0} value={MarkerNavType.Arrows}>
                    {t("navigation-type.arrows")}
                  </MenuItem>
                  <MenuItem key={1} value={MarkerNavType.Markers}>
                    {t("navigation-type.markers")}
                  </MenuItem>
                  <MenuItem key={2} value={MarkerNavType.None}>
                    {t("navigation-type.none")}
                  </MenuItem>
                </Select>
              </FormControl>
            </div>
          </ListItem>

          {/* Cloud Opacity */}
          <ListItem>
            <div className={classes.listItem}>
              <Typography gutterBottom variant="subtitle2">
                {t("user-settings.point-visibility")}
              </Typography>
              <SingleSlider
                value={savedOpacity}
                onChange={(value) => {
                  viewer?.setPointCloudOpacity(value);
                }}
                onFinish={(value) => {
                  dispatch(changeOpacity(value));
                }}
                min={0}
                max={1}
                step={0.01}
                magnitude={100}
                endAdornment={"%"}
              />
            </div>
          </ListItem>

          {/* Cloud Max Points */}
          <ListItem>
            <div className={classes.listItem}>
              <Typography gutterBottom variant="subtitle2">
                {t("user-settings.max-visible-points")}
              </Typography>
              <SingleSlider
                value={savedMaxPoints}
                onChange={(value) => {
                  viewer?.setPointCloudMaxPoints(value);
                }}
                onFinish={(value) => {
                  dispatch(changeCloudMaxPoints(value));
                }}
                min={0}
                max={10}
                step={0.05}
                endAdornment={"M"}
              />
            </div>
          </ListItem>

          {/* Cloud Distance */}
          <ListItem>
            <div className={classes.listItem}>
              <Typography gutterBottom variant="subtitle2">
                {t("user-settings.point-distance")}
              </Typography>
              <SingleSlider
                value={savedCloudDistance}
                onChange={(value) => {
                  viewer?.setPointCloudDistance(value);
                }}
                onFinish={(value) => {
                  dispatch(changeCloudDistance(value));
                }}
                min={0}
                max={1000}
                step={5}
                units={units}
                endAdornment={units}
                labelFunction={(value) => {
                  const maxValue = converter.convert(999, "m", units);
                  return (value > maxValue) ? "∞" : `${value}${units}`;
                }}
              />
            </div>
          </ListItem>

          {/* Height range */}
          {isHeightmap && (
            <ListItem>
              <div className={classes.listItem}>
                <Typography gutterBottom variant="subtitle2">
                  {t("user-settings.height-range")}
                </Typography>
                <RangeSlider
                  value={savedHeightClip}
                  onChange={(value) => {
                    viewer?.setPointCloudHeightClip(value);
                  }}
                  onFinish={(value) => {
                    dispatch(changeHeightClip(value));
                  }}
                  min={-0.5}
                  max={1.5}
                  step={0.01}
                  magnitude={100}
                  endAdornment={"%"}
                />
              </div>
            </ListItem>
          )}

          {/* Intensity range */}
          {isIntensity && (
            <ListItem>
              <div className={classes.listItem}>
                <Typography gutterBottom variant="subtitle2">
                  {t("user-settings.intensity-range")}
                </Typography>
                <RangeSlider
                  value={savedIntensityClip}
                  onChange={(value) => {
                    viewer?.setPointCloudIntensityClip(value);
                  }}
                  onFinish={(value) => {
                    dispatch(changeIntensityClip(value));
                  }}
                  min={-0.5}
                  max={1.5}
                  step={0.01}
                  magnitude={100}
                  endAdornment={"%"}
                />
              </div>
            </ListItem>
          )}

          {/* Circular Points */}
          <ListItem>
            <div className={classes.listItem}>
              <SwitchWithLabel
                label={t("user-settings.circular-points")}
                checked={savedCircularPoints}
                onChange={(_, checked) => {
                  dispatch(changeCircularPoints(checked));
                }}
              />
            </div>
          </ListItem>
        </List>

        <Divider variant="middle" />

        {/* Point Cloud Size */}
        <List dense className={classes.list}>
          <ListItem>
            <Typography className={classes.sectionTitle} gutterBottom>
              {t('user-settings.point-cloud-sizing')}
            </Typography>
          </ListItem>

          {/* Point cloud scale */}
          <ListItem>
            <div className={classes.listItem}>
              <Typography gutterBottom variant="subtitle2">
                {t("user-settings.point-size")}
              </Typography>
              <SingleSlider
                value={savedCloudScale}
                onChange={(value) => {
                  viewer?.setPointCloudScale(value);
                }}
                onFinish={(value) => {
                  dispatch(changeCloudScale(value));
                }}
                min={0}
                max={3}
                step={0.1}
              />
            </div>
          </ListItem>

          {/* Minimum point size */}
          <ListItem>
            <div className={classes.listItem}>
              <Typography gutterBottom variant="subtitle2">
                {t('user-settings.minimum-point-size')}
              </Typography>
              <SingleSlider
                value={savedCloudMinimum}
                onChange={(value) => {
                  viewer?.setPointCloudMinimum(value);
                }}
                onFinish={(value) => {
                  dispatch(changeCloudMinimum(value));
                }}
                min={0}
                max={3}
                step={0.1}
              />
            </div>
          </ListItem>

          {/* Adaptive point sizing */}
          <ListItem>
            <div className={classes.listItem}>
              <SwitchWithLabel
                label={t("user-settings.dynamic-point-sizing")}
                tooltip={t("user-settings.not-available-las")}
                checked={savedDynamicSize}
                onChange={(_, checked) => {
                  dispatch(changeDynamicSize(checked));
                }}
              />
            </div>
          </ListItem>
        </List>

        {/* Asset controls */}
        <List dense className={classes.list}>
          <ListItem>
            <Typography className={classes.sectionTitle} gutterBottom>
              {t('user-settings.tag_controls')}
            </Typography>
          </ListItem>

          {/* Tag Distance */}
          <ListItem>
            <div className={classes.listItem}>
              <Typography gutterBottom variant="subtitle2">
                {t("user-settings.tag-distance")}
              </Typography>
              <SingleSlider
                value={savedTagDistance}
                onChange={(value) => {
                  viewer?.setTagDistance(value);
                }}
                onFinish={(value) => {
                  dispatch(changeTagDistance(value));
                }}
                min={0}
                max={1000}
                step={5}
                units={units}
                endAdornment={units}
                labelFunction={(value) => {
                  const maxValue = converter.convert(999, "m", units);
                  return (value > maxValue) ? "∞" : `${value}${units}`;
                }}
              />
            </div>
          </ListItem>
        </List>


        <Divider variant="middle" />

        {/* Classifications */}
        <List>
          <ListItem>
            <div className={classes.listItem} style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
              <Typography
                gutterBottom
                variant="subtitle2"
                className={classes.sectionTitle}
              >
                {t("user-settings.classifications")}
              </Typography>

              {savedAvailableClassifications.length > 0 && (
                <ArrowTooltip
                  title={t("user-settings.toggle-all-classifications")}
                  placement="top"
                >
                  <Switch
                    checked={savedAvailableClassifications.every(
                      (classification) => classification.visible
                    )}
                    onChange={(_, checked) => {
                      dispatch(changeAllClassificationVisibility(checked));
                    }}
                  />
                </ArrowTooltip>
              )}
            </div>
          </ListItem>

          <ClassificationList
            classifications={savedAvailableClassifications}
          />

        </List>

      </SlimScrollbar>
    </Drawer>
  );
};